import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["list", "row", "optionInput", "addButton", "triggerLabel"];
  static values = {
    min: { type: Number, default: 2 },
    max: { type: Number, default: 6 },
    debounceMs: { type: Number, default: 400 },
  };

  connect() {
    this._refreshAddVisibility();
    this._onDocClick = (event) => {
      const details = this.element.querySelector("details");
      if (!details?.open) return;
      if (!details.contains(event.target)) details.open = false;
    };
    document.addEventListener("click", this._onDocClick, true);
  }

  disconnect() {
    if (this._onDocClick) {
      document.removeEventListener("click", this._onDocClick, true);
      this._onDocClick = null;
    }
    clearTimeout(this._saveTimer);
  }

  optionTyped(event) {
    const row = event.target.closest("[data-word-image-select-options-target='row']");
    const radio = row?.querySelector("input[type=radio]");
    if (radio) radio.value = event.target.value;
    if (radio?.checked) this._updateTriggerLabel(event.target.value);
    this._scheduleSave();
  }

  enterToAdd(event) {
    event.preventDefault();
    if (this._optionCount() < this.maxValue) {
      this.add();
    }
  }

  answerChanged(event) {
    const row = event.target.closest("[data-word-image-select-options-target='row']");
    const text = row?.querySelector("input[type=text]")?.value || "";
    this._updateTriggerLabel(text);
    this._scheduleSave();
  }

  add() {
    if (this._optionCount() >= this.maxValue) return;

    const tpl = document.createElement("li");
    tpl.className = "flex items-center gap-2";
    tpl.setAttribute("data-word-image-select-options-target", "row");
    const itemEl = this.element.closest("[data-item-id]");
    const itemId = itemEl?.dataset.itemId || "";
    const inputClasses = [
      "w-full rounded-ui border transition-colors duration-150",
      "placeholder:text-content-muted",
      "focus:outline-none focus:ring-1",
      "px-3 py-1.5 text-sm",
      "border-surface-border focus:border-brand-500 focus:ring-brand-500",
      "hover:border-surface-border-strong",
    ].join(" ");
    tpl.innerHTML = `
      <input type="radio" name="answer_for_${itemId}" value="" class="shrink-0"
             data-action="change->word-image-select-options#answerChanged">
      <div class="flex-1 min-w-0">
        <input type="text" value=""
               autocomplete="off"
               data-word-image-select-options-target="optionInput"
               data-action="input->word-image-select-options#optionTyped keydown.enter->word-image-select-options#enterToAdd"
               class="${inputClasses}">
      </div>
      <button type="button"
              data-action="click->word-image-select-options#remove"
              class="shrink-0 text-content-muted hover:text-error">×</button>
    `;
    this.listTarget.appendChild(tpl);
    tpl.querySelector("input[type=text]")?.focus();
    this._refreshAddVisibility();
  }

  remove(event) {
    const row = event.target.closest("[data-word-image-select-options-target='row']");
    if (!row) return;
    const radio = row.querySelector("input[type=radio]");
    const wasAnswer = !!radio?.checked;
    row.remove();
    if (wasAnswer) this._updateTriggerLabel("");
    this._refreshAddVisibility();
    this._scheduleSave();
  }

  _refreshAddVisibility() {
    if (!this.hasAddButtonTarget) return;
    this.addButtonTarget.classList.toggle("hidden", this._optionCount() >= this.maxValue);
  }

  _optionCount() {
    return this.rowTargets.length;
  }

  _updateTriggerLabel(text) {
    if (!this.hasTriggerLabelTarget) return;
    if (text && text.trim()) {
      this.triggerLabelTarget.textContent = text;
      this.triggerLabelTarget.classList.remove("text-content-muted");
    } else {
      this.triggerLabelTarget.textContent = this.triggerLabelTarget.dataset.placeholder || "";
      this.triggerLabelTarget.classList.add("text-content-muted");
    }
  }

  _scheduleSave() {
    clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(() => this._save(), this.debounceMsValue);
  }

  async _save() {
    const itemEl = this.element.closest("[data-item-id]");
    const url = itemEl?.dataset.itemUpdateUrl;
    if (!url) return;

    const token = document.querySelector("meta[name=csrf-token]")?.content;
    const formData = new FormData();
    const options = this.optionInputTargets
      .map((i) => i.value.trim())
      .filter((v) => v.length > 0);
    if (options.length === 0) {
      formData.append("item[options][]", "");
    } else {
      options.forEach((o) => formData.append("item[options][]", o));
    }
    const checked = this.element.querySelector("input[type=radio]:checked");
    const checkedRow = checked?.closest("[data-word-image-select-options-target='row']");
    const answer = checkedRow?.querySelector("input[type=text]")?.value.trim() || "";
    formData.append("item[answer]", answer);

    await fetch(url, {
      method: "PATCH",
      headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": token || "" },
      body: formData,
    });
  }
}
